<template>
  <svg
    class="minimal-icon"
    :width="size"
    :height="size"
    viewBox="0 0 24 24"
    :fill="filled ? 'currentColor' : 'none'"
    :stroke="filled ? 'none' : 'currentColor'"
    :stroke-width="strokeWidth"
    stroke-linecap="round"
    stroke-linejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    <template v-for="(shape, index) in shapes" :key="index">
      <circle
        v-if="shape[0] === 'c'"
        :cx="shape[1]"
        :cy="shape[2]"
        :r="shape[3]"
      />
      <path v-else :d="shape[1]" />
    </template>
  </svg>
</template>

<script setup>
import { computed, defineProps } from 'vue'

/*
 * Iconos minimalistas de línea (24x24, stroke). Reemplazan a Font Awesome en
 * el navbar. Cada entrada es una lista de formas: ['p', d] para path y
 * ['c', cx, cy, r] para círculo.
 */
const ICONS = {
  box: [['p', 'm12 3 9 5-9 5-9-5z'], ['p', 'M3 8v9l9 5 9-5V8M12 13v9M7.5 5.5l9 5V15']],
  receipt: [['p', 'M6 3h12v18l-3-2-3 2-3-2-3 2zM9 7h6M9 11h6M9 15h3']],
  users: [['c', 9, 8, 3], ['p', 'M3 20v-2a6 6 0 0 1 12 0v2M16 5a3 3 0 0 1 0 6M18 14a5 5 0 0 1 3 4v2']],
  shield: [['p', 'M12 3 4 6v6c0 5 8 9 8 9s8-4 8-9V6zM8 12l3 3 5-6']],
  bell: [['p', 'M18 8a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9M10 21h4']],
  clock: [['c', 12, 12, 9], ['p', 'M12 7v5l3 2']],
  settings: [['p', 'M4 7h4M12 7h8M4 17h8M16 17h4'], ['c', 10, 7, 2], ['c', 14, 17, 2]],
  logout: [['p', 'M10 4H4v16h6M9 12h12M17 8l4 4-4 4']],
  search: [['c', 11, 11, 6.5], ['p', 'M16 16l4.5 4.5']],
  close: [['p', 'M6.5 6.5l11 11'], ['p', 'M17.5 6.5l-11 11']],
  user: [['c', 12, 8.5, 3.6], ['p', 'M4.8 19.8c1.4-3.4 4-5.1 7.2-5.1s5.8 1.7 7.2 5.1']],
  home: [['p', 'M4 10.6 12 4.2l8 6.4V19a1 1 0 0 1-1 1h-4.2v-5.6H9.2V20H5a1 1 0 0 1-1-1z']],
  catalog: [
    ['p', 'M11.4 6.2A2.6 2.6 0 0 0 9.2 4.8H4.6v13.4h4.6a2.6 2.6 0 0 1 2.2 1.3z'],
    ['p', 'M12.6 6.2a2.6 2.6 0 0 1 2.2-1.4h4.6v13.4h-4.6a2.6 2.6 0 0 0-2.2 1.3z']
  ],
  gallery: [
    ['p', 'M4 5.4h16v13.2H4z'],
    ['c', 9, 10, 1.4],
    ['p', 'M4.6 17.4l4.6-4.2 3.4 2.6 2.8-2.2 4 3.6']
  ],
  blog: [
    ['p', 'M4.4 19.6h15.2'],
    ['p', 'M5.4 19.2l.9-3.8L15.6 6a2 2 0 0 1 2.9 2.9l-8.6 8.5z'],
    ['p', 'M14.2 7.6l2.4 2.4']
  ],
  truck: [
    ['p', 'M3.2 6.8h11v9.4h-11z'],
    ['p', 'M14.2 10h3.6l3 3.1v3.1h-6.6z'],
    ['c', 7, 17.6, 1.5],
    ['c', 17.4, 17.6, 1.5]
  ],
  leaf: [
    ['p', 'M20 4.4c.6 8.6-4.2 13.4-12.2 14.4-.5-7.4 4.2-13 12.2-14.4z'],
    ['p', 'M8.4 18.2c1-4.2 3.9-7.2 8-9.2']
  ],
  sparkle: [
    ['p', 'M12 3.4l1.7 5.1 5.1 1.7-5.1 1.7-1.7 5.1-1.7-5.1L5.2 10.2l5.1-1.7z'],
    ['p', 'M18.4 16.6l.7 2 2 .7-2 .7-.7 2-.7-2-2-.7 2-.7z']
  ],
  store: [
    ['p', 'M4.4 9.6 6 4.8h12l1.6 4.8'],
    ['p', 'M5.2 9.6V20h13.6V9.6'],
    ['p', 'M9.6 20v-5.4h4.8V20']
  ],
  trophy: [
    ['p', 'M18 2H6v7a6 6 0 0 0 12 0z'],
    ['p', 'M6 9H4.5a2.5 2.5 0 0 1 0-5H6'],
    ['p', 'M18 9h1.5a2.5 2.5 0 0 0 0-5H18'],
    ['p', 'M10 14.7V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22'],
    ['p', 'M14 14.7V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22'],
    ['p', 'M4 22h16']
  ],
  star: [
    ['p', 'M12 17.27 18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z']
  ],
  arrow: [['p', 'M4.6 12h14'], ['p', 'M13.4 6.8 18.6 12l-5.2 5.2']],
  arrowUpRight: [['p', 'M7 17 17 7'], ['p', 'M7.4 7h9.6v9.6']],
  comment: [['p', 'M4.2 5.8h15.6v10.4h-9.2l-4 3.2v-3.2H4.2z'], ['p', 'M8 9.6h8M8 12.6h5']],
  arrowLeft: [['p', 'M19.4 12h-14'], ['p', 'M10.6 6.8 5.4 12l5.2 5.2']],
  chevronLeft: [['p', 'M14.4 6.6 9 12l5.4 5.4']],
  chevronRight: [['p', 'M9.6 6.6 15 12l-5.4 5.4']],
  chevronDown: [['p', 'M6.6 9.6 12 15l5.4-5.4']],
  check: [['p', 'M5.4 12.6l4.2 4.2 9-9']],
  filter: [['p', 'M4.4 6.6h15.2'], ['p', 'M7.2 12h9.6'], ['p', 'M10.2 17.4h3.6']],
  play: [['p', 'M9.6 7.8 16.4 12l-6.8 4.2z']],
  instagram: [
    ['p', 'M7.8 3.6h8.4a4.2 4.2 0 0 1 4.2 4.2v8.4a4.2 4.2 0 0 1-4.2 4.2H7.8a4.2 4.2 0 0 1-4.2-4.2V7.8a4.2 4.2 0 0 1 4.2-4.2z'],
    ['c', 12, 12, 3.9],
    ['p', 'M16.9 7.1h.01']
  ]
}

const props = defineProps({
  name: { type: String, required: true },
  size: { type: [Number, String], default: 18 },
  strokeWidth: { type: [Number, String], default: 1.5 },
  // Formas rellenas (ej: el triángulo de play)
  filled: { type: Boolean, default: false }
})

const shapes = computed(() => ICONS[props.name] || [])
</script>

<style scoped>
.minimal-icon {
  display: block;
  flex: 0 0 auto;
}
</style>
